import { Popover as PopoverPrimitive } from "radix-ui";
import * as React from "react";

import Icon from "@/components/icon";
import { cn } from "@/utils/cn";

export type ComboboxOption = {
  value: string;
  label: string;
  /** Shown to the right of the label, greyed. */
  hint?: string;
  /** Matched by the search box besides the label (a country code, say). */
  keywords?: string;
};

/**
 * A select with a search box, for lists too long to scroll (every country a provider
 * sells numbers in). Radix's own Select swallows typing for its jump-to-letter, so this
 * is a popover holding a plain input and a filtered list.
 */
export function Combobox({
  options,
  value,
  onChange,
  placeholder = "Pick one",
  searchPlaceholder = "Search",
  emptyText = "Nothing matches",
  disabled,
  className,
  "aria-label": ariaLabel,
}: {
  options: ComboboxOption[];
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  className?: string;
  "aria-label"?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [active, setActive] = React.useState(0);
  const listRef = React.useRef<HTMLDivElement | null>(null);

  const shown = React.useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((o) => `${o.label} ${o.keywords ?? ""}`.toLowerCase().includes(needle));
  }, [options, query]);

  // A fresh search starts at the top; the highlight never points past the list.
  React.useEffect(() => setActive(0), [query]);
  React.useEffect(() => {
    if (!open) setQuery("");
  }, [open]);
  React.useEffect(() => {
    listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: "nearest" });
  }, [active, shown]);

  const chosen = options.find((o) => o.value === value);
  const pick = (option: ComboboxOption) => {
    onChange(option.value);
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => {
        const next = e.key === "ArrowDown" ? i + 1 : i - 1;
        return Math.max(0, Math.min(shown.length - 1, next));
      });
    } else if (e.key === "Enter") {
      e.preventDefault();
      const option = shown[active];
      if (option) pick(option);
    }
  };

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger
        type="button"
        role="combobox"
        // Fields inside a List or Card take the page grey; global.css keys that on the
        // slot, so without it this trigger would melt into the card it sits on.
        data-slot="select-trigger"
        aria-label={ariaLabel}
        aria-expanded={open}
        disabled={disabled}
        className={cn(
          "flex h-(--control-height) w-full items-center justify-between gap-2 rounded-lg border-2 border-transparent bg-tg-section-bg ps-4 pe-3 text-start text-sm text-foreground transition-colors outline-none select-none focus-visible:border-ring/40 disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
      >
        <span className={cn("line-clamp-1", !chosen && "text-muted-foreground")}>
          {chosen ? (
            <>
              {chosen.label}
              {chosen.hint && <span className="text-muted-foreground"> · {chosen.hint}</span>}
            </>
          ) : (
            placeholder
          )}
        </span>
        <Icon name="chevronDown" size={16} className="shrink-0 text-muted-foreground" />
      </PopoverPrimitive.Trigger>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={4}
          className="z-50 w-(--radix-popover-trigger-width) overflow-hidden rounded-xl bg-popover p-1 text-popover-foreground shadow-md ring-1 ring-foreground/10 duration-100 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0"
          onKeyDown={onKeyDown}
        >
          <div className="relative p-1">
            <Icon
              name="search"
              size={16}
              className="pointer-events-none absolute start-3.5 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              // On the popover's own surface, so it takes the grey directly.
              className="h-(--control-height-sm) w-full rounded-lg bg-muted ps-9 pe-3 text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>
          <div ref={listRef} className="max-h-72 overflow-y-auto">
            {shown.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-muted-foreground">{emptyText}</p>
            ) : (
              shown.map((option, i) => (
                <button
                  key={option.value}
                  type="button"
                  data-active={i === active}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => pick(option)}
                  className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-start text-sm outline-none data-[active=true]:bg-muted"
                >
                  <span className="line-clamp-1 flex-grow">{option.label}</span>
                  {option.hint && (
                    <span className="shrink-0 text-xs text-muted-foreground tabular-nums">{option.hint}</span>
                  )}
                  {option.value === value && <Icon name="check" size={16} className="shrink-0 text-primary" />}
                </button>
              ))
            )}
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
